import React, { useState } from 'react';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Button,
  CircularProgress,
  Collapse,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

export type PrerequisiteStatus = 'pending' | 'met' | 'unmet';

export interface PrerequisiteItem {
  detail?: string;
  label: string;
  status: PrerequisiteStatus;
}

interface Props {
  hasSyncFailed: boolean;
  isSyncing: boolean;
  items: Array<PrerequisiteItem>;
  onRetrySync: () => void;
  syncFailureMessage?: string;
  syncProgressPercent: number;
}

function getPrerequisiteStatusIcon(status: PrerequisiteStatus) {
  if (status === 'met') {
    return <CheckCircleIcon fontSize="small" color="success" />;
  }

  if (status === 'unmet') {
    return <CancelOutlinedIcon fontSize="small" color="error" />;
  }

  return <CircularProgress size={16} />;
}

export default function PrerequisitesStep({
  hasSyncFailed,
  isSyncing,
  items,
  onRetrySync,
  syncFailureMessage,
  syncProgressPercent,
}: Props) {
  const [expandedLabels, setExpandedLabels] = useState<Array<string>>([]);

  function toggleExpanded(label: string) {
    setExpandedLabels((prev) => (prev.includes(label) ? prev.filter((item) => item !== label) : prev.concat(label)));
  }

  return (
    <Stack spacing={2}>
      {hasSyncFailed ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetrySync}>
              Retry
            </Button>
          }
        >
          Unable to sync the latest Salesforce data{syncFailureMessage ? `: ${syncFailureMessage}` : '.'}
        </Alert>
      ) : (
        isSyncing && (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Syncing the latest Salesforce data. This can take up to 4 minutes.
            </Typography>
            <LinearProgress value={syncProgressPercent} variant="determinate" />
          </Stack>
        )
      )}

      <List dense>
        {items.map((item) => {
          const canExpand = item.status === 'unmet' && !!item.detail;
          const isExpanded = canExpand && expandedLabels.includes(item.label);

          return (
            <React.Fragment key={item.label}>
              <ListItemButton
                disableRipple={!canExpand}
                onClick={canExpand ? () => toggleExpanded(item.label) : undefined}
                sx={{ cursor: canExpand ? 'pointer' : 'default', px: 0 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>{getPrerequisiteStatusIcon(item.status)}</ListItemIcon>
                <ListItemText primary={item.label} />
                {canExpand && (isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
              </ListItemButton>
              {canExpand && (
                <Collapse in={isExpanded} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 4, pb: 1 }}>
                    {item.detail}
                  </Typography>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Stack>
  );
}
